import React, { useState, useEffect } from 'react';
import { Trash2, Book, TrendingUp } from 'lucide-react';
import { FaHeart, FaRegHeart, FaComment } from 'react-icons/fa';
import { BiDotsHorizontalRounded, BiTrash, BiFlag } from 'react-icons/bi';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getProfileImageUrl } from '../utils/getProfileImageUrl';
import '../styles/Discover.css';

const Discover = () => {
    const [posts, setPosts] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [books, setBooks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [reportPostId, setReportPostId] = useState(null);
    const [reportReason, setReportReason] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [deletePostId, setDeletePostId] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const navigate = useNavigate();
    const currentUserId = localStorage.getItem('userId');

    useEffect(() => {
        const fetchDiscoverPosts = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
        
            try {
                const response = await api.get('/discover/posts', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                
                if (response.data) {
                    const { posts = [] } = response.data;
    
                    const updatedPosts = posts.map(post => ({
                        ...post,
                        profile_image_url: getProfileImageUrl(post.profile_image_url),
                        isLiked: post.is_liked,
                        comments: post.comments.map(comment => ({
                            ...comment,
                            isLiked: comment.is_liked // Yorumlar için isLiked ayarı
                        })),
                    }));
                   
                    setPosts(updatedPosts);
                }
                setIsLoading(false); 
            } catch (error) {
                console.error('Error fetching posts:', error);
                setError('Failed to fetch posts. Please try again.');
                if (error.response?.status === 401) {
                    localStorage.removeItem('token');
                    navigate('/login');
                }
                setIsLoading(false); 
            }
        };
    
        const fetchAuthorsAndBooks = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
    
            try {
                const response = await api.get('/discover/authors-books', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
    
                if (response.data) {
                    const { authors = [], books = [] } = response.data;
                    setAuthors(authors);
                    setBooks(books);
                }
            } catch (error) {
                console.error('Error fetching authors and books:', error);
            }
        };

        const fetchCurrentUser = async () => {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('userId');
            if (!token || !userId) {
                navigate('/login');
                return;
            }

            try {
                const response = await api.get(`/profile/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const currentUserData = response.data.user;
                const currentUserProfilePhoto = getProfileImageUrl(currentUserData.profile_image_url);
                 
                setCurrentUser({
                    username: currentUserData.username,
                    profilePhoto: currentUserProfilePhoto,
                });
            } catch (error) {
                console.error("Error fetching current user's details:", error);
            }
        };

      
    fetchDiscoverPosts();
    fetchAuthorsAndBooks();
        fetchCurrentUser();
    }, [navigate]);

    const handleLike = async (postId, commentId = null) => {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        if (!token || !userId || isNaN(postId)) {
            console.error('Geçersiz token, userId veya postId');
            navigate('/login');
            return;
        }
    
        try {
           
            const url =
              commentId === null
                ? `/discover/posts/${postId}/like`
                : `/discover/comments/${commentId}/like`;
    
            const payload = { userId: parseInt(userId) };
           
    
            await api.post(url, payload, {
              headers: { Authorization: `Bearer ${token}` },
            });
    
            setPosts((prevPosts) =>
              prevPosts.map((post) => {
                if (commentId === null && post.post_id === postId) {
                  const yeniDurum = !post.isLiked;
                
    
                  return {
                    ...post,
                    likes_count: post.isLiked ? post.likes_count - 1 : post.likes_count + 1,
                    isLiked: yeniDurum, // isLiked durumunu toggle et
                  };
                } else if (post.post_id === postId) {
                  return {
                    ...post,
                    comments: post.comments.map((comment) => {
                      if (comment.comment_id === commentId) {
                        const commentYeniDurum = !comment.isLiked;
                        
    
                        return {
                          ...comment,
                          likes_count: comment.isLiked ? comment.likes_count - 1 : comment.likes_count + 1,
                          isLiked: commentYeniDurum, // isLiked durumunu toggle et
                        };
                      }
                      return comment;
                    }),
                  };
                }
                return post;
              })
            );
        } catch (error) {
            console.error('Post veya yorumun beğenisini değiştirme hatası:', error);
            console.error('Hata detayı:', error.response ? error.response.data : error.message);
        }
    };

    const handleCommentSubmit = async (postId, e) => {
        e.preventDefault();
        const post = posts.find((p) => p.post_id === postId);

        if (!post.newComment.trim() || !currentUser) return;

        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');

        if (!token || !userId) {
            console.error('Invalid token or userId');
            navigate('/login');
            return;
        }

        try {
            const response = await api.post(
                `/discover/posts/${postId}/comments`,
                {   
                    userId: parseInt(userId),
                    postId,
                    content: post.newComment,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
               
            const newComment = {
                comment_id: response.data.commentId,
                user_id: parseInt(userId),
                profile_image_url: getProfileImageUrl(currentUser.profilePhoto) || '/images/default-avatar.jpg',
                username: currentUser.username,
                content: post.newComment,
                created_at: new Date().toISOString(),
                likes_count: 0,
                isLiked: false,
            };

            setPosts((prevPosts) =>
                prevPosts.map((p) =>
                    p.post_id === postId ? { ...p, comments: [...p.comments, newComment], newComment: '' } : p
                )
            );
        } catch (error) {
            console.error('Error adding post comment:', error);
            console.error('Error details:', error.response ? error.response.data : error.message);
        }
    };

    const handleCommentChange = (postId, value) => {
        setPosts((prevPosts) =>
            prevPosts.map((post) =>
                post.post_id === postId ? { ...post, newComment: value } : post
            )
        );
    };

    const handleDeleteComment = async (postId, commentId) => {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');

        if (!token || !userId || isNaN(commentId)) {
            console.error('Invalid token, userId, or commentId');
            navigate('/login');
            return;
        }

        try {
            await api.delete(`/discover/comments/${commentId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setPosts((prevPosts) =>
                prevPosts.map((post) =>
                    post.post_id === postId
                        ? {
                            ...post,
                            comments: post.comments.filter((comment) => comment.comment_id !== commentId),
                        }
                        : post
                )
            );
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };

    const confirmDeletePost = (postId) => {
        setDeletePostId(postId);
        setShowDeleteConfirm(true);
    };

    const handleDeletePost = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            await api.delete(`/discover/posts/${deletePostId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setPosts((prevPosts) => prevPosts.filter((post) => post.post_id !== deletePostId));
        } catch (error) {
            console.error('Error deleting post:', error);
        } finally {
            setShowDeleteConfirm(false);
        }
    };

    const handleReportPost = (postId) => {
        setReportPostId(postId);
        setShowReportModal(true);
    };

    const sendReport = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            await api.post(
                `/discover/posts/${reportPostId}/report`,
                {
                    reason: reportReason,
                    description: reportDescription
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setShowSuccessModal(true);
        } catch (error) {
            console.error('Error reporting post:', error);
        } finally {
            setShowReportModal(false);
        }
    };

    const followAuthor = async (authorId) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const response = await api.post(
                '/discover/follow',
                { authorId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

          

            if (response.data.success) {
                setAuthors((prevAuthors) =>
                    prevAuthors.map((a) =>
                        a.user_id === authorId
                            ? {
                                ...a,
                                is_following: true,
                                followers_count: a.followers_count + 1,
                            }
                            : a
                    )
                );
            }
        } catch (error) {
            console.error('Error following author:', error);
        }
    };

    const unfollowAuthor = async (authorId) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const response = await api.post(
                '/discover/unfollow',
                { authorId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

           

            if (response.data.success) {
                setAuthors((prevAuthors) =>
                    prevAuthors.map((a) =>
                        a.user_id === authorId
                            ? {
                                ...a,
                                is_following: false,
                                followers_count: a.followers_count - 1,
                            }
                            : a
                    )
                );
            }
        } catch (error) {
            console.error('Error unfollowing author:', error);
        }
    };

    const toggleComments = (postId) => {
        setPosts((prevPosts) =>
            prevPosts.map((post) => (post.post_id === postId ? { ...post, showComments: !post.showComments } : post))
        );
    };

    const togglePostMenu = (postId) => {
        setPosts((prevPosts) =>
            prevPosts.map((post) =>
                post.post_id === postId
                    ? { ...post, showMenu: !post.showMenu }
                    : { ...post, showMenu: false } // Close menus of other posts
            )
        );
    };

    const toggleCommentMenu = (postId, commentId) => {
        setPosts((prevPosts) =>
            prevPosts.map((post) =>
                post.post_id === postId
                    ? {
                        ...post,
                        comments: post.comments.map((comment) =>
                            comment.comment_id === commentId
                                ? { ...comment, showMenu: !comment.showMenu }
                                : { ...comment, showMenu: false } // Close menus of other comments
                        ),
                    }
                    : post
            )
        );
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;

    return (
        <>
            <div className={`discover-container ${showReportModal || showDeleteConfirm || showSuccessModal ? 'modal-open' : ''}`}>
                <div className="discover-main">
                    <h2 className="discover-heading">Keşfet</h2>
                    <div className="posts-container">
                        {posts.map((post) => (
                            <div key={post.post_id} className="post-card">
                                <div className="post-header">
                                    <img src={getProfileImageUrl(post.avatar) } alt={post.username} className="avatar" />
                                    <div className="post-user-info">
                                        <div className="user-info-header">
                                            <Link to={`/profile/${post.user_id}`} className="author-name">{post.username}</Link>
                                            <span className="username">@{post.username}</span>
                                        </div>
                                        <span className="post-date">{new Date(post.created_at).toLocaleString('tr-TR')}</span>
                                    </div>
                                    <div className="post-menu">
                                        <button className="menu-button" onClick={() => togglePostMenu(post.post_id)}>
                                            <BiDotsHorizontalRounded />
                                        </button>
                                        {post.showMenu && (
                                            <div className="dropdown-menu">
                                                {parseInt(post.user_id) === parseInt(currentUserId) && (
                                                    <button className="dropdown-item" onClick={() => confirmDeletePost(post.post_id)}>
                                                        Sil
                                                    </button>
                                                )}
                                                <button className="dropdown-item" onClick={() => handleReportPost(post.post_id)}>
                                                    Şikayet Et
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="post-content">
                                    <p>{post.content}</p>
                                    <div className="post-actions">
                                        <span className="post-timestamp">{new Date(post.created_at).toLocaleString('tr-TR')}</span>
                                        <div className="interaction-buttons">
                                            <button className="action-button" onClick={() => handleLike(post.post_id)}>
                                                {post.isLiked ? <FaHeart color="#ff4b4b" /> : <FaRegHeart />}
                                                <span>{isNaN(post.likes_count) ? 0 : post.likes_count}</span>
                                            </button>
                                            <button className="action-button" onClick={() => toggleComments(post.post_id)}>
                                                <FaComment />
                                                <span>{post.comments.length}</span>
                                            </button>
                                        </div>
                                    </div>
                                    {post.showComments && (
                                        <div className="comments-section">
                                            {post.comments.map((comment) => (
                                                <div key={comment.comment_id} className="comment">
                                                    <div className="comment-header">
                                                        <img
                                                            src={getProfileImageUrl(comment.profile_image_url)}
                                                            alt={comment.username}
                                                            className="comment-avatar"
                                                        />
                                                        <div className="comment-info">
                                                            <div className="user-info-header">
                                                                <Link to={`/profile/${comment.user_id}`} className="comment-author">
                                                                    {comment.username}
                                                                </Link>
                                                                <span className="username">@{comment.username}</span>
                                                            </div>
                                                            <span className="comment-date">{new Date(comment.created_at).toLocaleString('tr-TR')}</span>
                                                        </div>
                                                        <div className="comment-menu">
                                                            <button className="menu-button" onClick={() => toggleCommentMenu(post.postId, comment.comment_id)}>
                                                                <BiDotsHorizontalRounded />
                                                            </button>
                                                            {comment.showMenu && (
                                                                <div className="dropdown-menu">
                                                                    {parseInt(comment.user_id) === parseInt(currentUserId) && (
                                                                        <button className="dropdown-item" onClick={() => handleDeleteComment(post.post_id, comment.comment_id)}>
                                                                            Sil
                                                                        </button>
                                                                    )}
                                                                    <button className="dropdown-item" onClick={() => handleReportPost(comment.comment_id)}>
                                                                        Şikayet Et
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="comment-body">
                                                        <span className="comment-content">{comment.content}</span>
                                                    </div>
                                                    <div className="comment-footer">
                                                        <button
                                                            className="action-button"
                                                            onClick={() => handleLike(post.post_id, comment.comment_id)}>
                    
                                                            {comment.isLiked ? <FaHeart color="#ff4b4b" /> : <FaRegHeart />}
                                                            <span>{isNaN(comment.likes_count) ? 0 : comment.likes_count}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            <form className="comment-form" onSubmit={(e) => handleCommentSubmit(post.post_id, e)}>
                                                <input
                                                    type="text"
                                                    placeholder="Yorumunuzu yazın..."
                                                    value={post.newComment}
                                                    onChange={(e) => handleCommentChange(post.post_id, e.target.value)}
                                                />
                                                <button type="submit">Gönder</button>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="discover-sidebar">
                    <div className="sidebar-section authors-section">
                        <h3>Önerilen Yazarlar</h3>
                        {authors.map((author) => (
                            <div key={author.user_id} className="author-card">
                                <div className="author-info">
                                    <img src={getProfileImageUrl(author.profile_image_url)} alt={author.full_name} className="avatar" />
                                    <div className="author-details">
                                        <Link to={`/profile/${author.user_id}`} className="author-name">{author.full_name}</Link>
                                        <span className="author-followers">{author.followers_count} takipçi</span>
                                    </div>
                                </div>
                                <button
                                    className={`follow-button ${author.is_following ? 'following' : ''}`}
                                    onClick={() => {
                                        if (author.is_following) {
                                            unfollowAuthor(author.user_id);
                                        } else {
                                            followAuthor(author.user_id);
                                        }
                                    }}
                                >
                                    {author.is_following ? 'Takip Ediliyor' : 'Takip Et'}
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="sidebar-section trending-section">
                        <h3>Trend Kitaplar</h3>
                        {books.map((book) => (
                            <div key={book.book_id} className="trending-book">
                                <Book className="w-5 h-5 text-[#310034]" />
                                <div className="book-info">
                                    <Link to={`/book/${book.book_id}`} className="book-title">{book.title}</Link>
                                    <span className="book-author">{book.author}</span>
                                    <div className="book-readers">
                                        <TrendingUp className="w-4 h-4" />
                                        <span>{book.total_views} okuyucu</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {showReportModal && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={() => setShowReportModal(false)}>&times;</span>
                        <h2>Şikayet Et</h2>
                        <label>
                            Şikayet Sebebi:
                            <select value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                                <option value="">Bir sebep seçin</option>
                                <option value="İntihar veya kendine zarar verme">İntihar veya kendine zarar verme</option>
                                <option value="Yasal düzenlemeye tabi veya yasadışı">Yasal düzenlemeye tabi veya yasadışı</option>
                                <option value="Çıplaklık veya cinsellik">Çıplaklık veya cinsellik</option>
                                <option value="Nefret söylemi veya sembolleri">Nefret söylemi veya sembolleri</option>
                                <option value="Şiddet veya tehlikeli örgüt">Şiddet veya tehlikeli örgüt</option>
                                <option value="Zorbalık veya taciz">Zorbalık veya taciz</option>
                                <option value="Sahtekarlık veya dolandırıcılık">Sahtekarlık veya dolandırıcılık</option>
                                <option value="Fikri mülkiyet ihlali">Fikri mülkiyet ihlali</option>
                                <option value="Bunların Dışında (Diğer)">Bunların Dışında (Diğer)</option>
                            </select>
                        </label>
                        <label>
                            Açıklama:
                            <textarea
                                value={reportDescription}
                                onChange={(e) => setReportDescription(e.target.value)}
                                placeholder="Açıklama girin"
                            />
                        </label>
                        <button className="report-submit" onClick={sendReport}>Gönder</button>
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={() => setShowDeleteConfirm(false)}>&times;</span>
                        <h2>Paylaşımı Sil</h2>
                        <p>Bu paylaşımı silmek istediğinizden emin misiniz?</p>
                        <div className="modal-actions">
                            <button className="delete-submit" onClick={handleDeletePost}>Evet</button>
                            <button className="cancel-submit" onClick={() => setShowDeleteConfirm(false)}>Hayır</button>
                        </div>
                    </div>
                </div>
            )}

            {showSuccessModal && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={() => setShowSuccessModal(false)}>&times;</span>
                        <h2>Şikayet Alındı</h2>
                        <p>Şikayetiniz başarıyla alındı. Teşekkür ederiz.</p>
                        <button className="report-submit" onClick={() => setShowSuccessModal(false)}>Tamam</button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Discover; 